import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { SuggestionList, PeopleList } from "@/components/FindEmail";
import { Progress } from "@/components/ui/progress";
import { api, fullName, ago } from "@/lib/api";

export default function FindEmails() {
  const [params] = useSearchParams();
  const jobId = params.get("job");
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);
  const load = () => api.get("/email-suggestions").then((r) => setRows(r.data.filter((l) => !l.email || !l.first_name)));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!jobId) return;
    let t;
    const poll = async () => {
      const { data } = await api.get(`/finder-jobs/${jobId}`); setJob(data); load();
      if (data.status === "running") t = setTimeout(poll, 3000);
    };
    poll(); return () => clearTimeout(t);
  }, [jobId]);
  const drop = (id) => setRows((r) => r.filter((x) => x.id !== id));
  const dismiss = async (id) => { await api.post(`/leads/${id}/dismiss-suggestions`); drop(id); };
  const withHits = rows.filter((r) => r.email_suggestions?.length || r.people_suggestions?.length);
  return (
    <div>
      <PageHeader eyebrow="Leads" title="Find missing emails & contacts" />
      <div className="p-6 lg:p-10 space-y-5">
        <p className="text-sm text-slate-500 max-w-3xl">Emails and named people published on each company's website (home, contact, about and team pages). People are picked out by AI. Click an email or a person to save them to the lead, or dismiss the suggestions. Nothing is saved automatically. Addresses on the company's own domain are shown in blue.</p>
        {job && (
          <div className="bg-white border rounded-xl p-4 space-y-2 max-w-xl" data-testid="finder-job-progress">
            <div className="text-sm"><b>{job.done}</b> of {job.total} websites checked · <b>{job.found}</b> with emails {job.status === "finished" && <span className="text-emerald-700 ml-1">Finished</span>}</div>
            <Progress value={job.total ? (100 * job.done) / job.total : 100} />
          </div>
        )}
        <div className="label-xs" data-testid="suggestions-count">{withHits.length} lead(s) with suggestions · {rows.length - withHits.length} with none found</div>
        <div className="bg-white border rounded-xl divide-y" data-testid="suggestions-list">
          {rows.map((l) => (
            <div key={l.id} className="p-4 flex flex-wrap items-start gap-4" data-testid={`suggestion-row-${l.id}`}>
              <div className="w-72 shrink-0">
                <Link to={`/leads/${l.id}`} className="font-semibold hover:text-blue-600">{l.company_name}</Link>
                <div className="text-xs text-slate-400 truncate">{fullName(l)} · {l.website} · checked {ago(l.email_searched_at)}</div>
              </div>
              <div className="flex-1 min-w-[240px] space-y-2">{l.email_search_error ? <span className="text-xs text-red-600">{l.email_search_error}</span> : <>
                {!l.email && <SuggestionList lead={l} emails={l.email_suggestions} onSaved={() => load()} />}
                {!l.first_name && <div><div className="label-xs mb-1">People on the website</div>{l.people_suggestions?.length ? <PeopleList lead={l} people={l.people_suggestions} onSaved={() => load()} /> : <span className="text-xs text-slate-400">No named people found</span>}</div>}
              </>}</div>
              <button className="text-slate-400 hover:text-red-600" title="Dismiss" onClick={() => dismiss(l.id)} data-testid={`dismiss-suggestion-${l.id}`}><X size={16} /></button>
            </div>
          ))}
          {!rows.length && <div className="p-10 text-center text-sm text-slate-500" data-testid="suggestions-empty">No searches yet. On the Leads page, filter Email: "No email", select leads, then click "Find emails".</div>}
        </div>
      </div>
    </div>
  );
}
